/**
 * photo-fit.js -- measure the visible area of a cut-out product photo and scale it into a box.
 * Owner: section-bauer Produktseite (Projektleiter 02.10.2026). Other sections include it and do
 * not change it; change requests go through the Projektleiter.
 *
 * No dependencies. Read-only on the photo: a small copy (e.g. image_url width 160) is drawn into a
 * canvas (the Shopify CDN sends access-control-allow-origin: *) and the non-transparent area is
 * measured. Writes nothing but CSS custom properties on the element you pass in.
 *
 * API (window.CryonosPhotoFit):
 *   probe(url) -> Promise<null | { ar, cut, l, t, r, b }>
 *     ar = width / height of the photo; cut = true when its border is transparent (cut-out photo);
 *     l, t, r, b = visible area as fractions 0..1 of width / height. Cached per URL.
 *   fitValues(W, H, bb, opts) -> null | { fx, fy, fs, sw }
 *     Scale for an <img> that fills a W x H box with object-fit: contain and
 *     transform: translate(var(--fx), var(--fy)) scale(var(--fs)) (transform-origin centre), so that
 *     the visible area fills opts.w x opts.h of the box (fractions), standing on the floor line
 *     opts.base * H (fraction from the top); opts.max caps the scale; the scale is never below
 *     opts.min (default 1; pass e.g. 0.1 when photos must also shrink, as in to-scale rows).
 *     opts.floor = true keeps the floor line even for wide, low photos (rows on a common baseline);
 *     without it such photos sit slightly below the centre.
 *     sw = suggested floor-shadow width in % of W. null when the photo is not a cut-out.
 *   apply(el, values, fallbackScale) -- sets --fx, --fy, --fs, --sw on el (or clears them and sets
 *     --fs to fallbackScale when values is null).
 *   auto(root) -- every element [data-photo-fit="<probe url>"] inside root (default document) is
 *     fitted into its own box; options via data-fit-w / data-fit-h / data-fit-base / data-fit-max
 *     / data-fit-floor="1" (defaults .9 / .92 / .965 / 3 / off). Adds the class is-cut to the element when the photo is a
 *     cut-out. Re-runs on resize (ResizeObserver).
 */
(function () {
  if (window.CryonosPhotoFit) return;
  var cache = {};

  function probe(url) {
    if (!url) return Promise.resolve(null);
    if (cache[url]) return cache[url];
    cache[url] = new Promise(function (resolve) {
      var im = new Image();
      im.crossOrigin = 'anonymous';
      im.decoding = 'async';
      im.onload = function () {
        try {
          var w = im.naturalWidth, h = im.naturalHeight;
          var c = document.createElement('canvas');
          c.width = w; c.height = h;
          var ctx = c.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(im, 0, 0);
          var d = ctx.getImageData(0, 0, w, h).data;
          var l = w, t = h, r = -1, b = -1, edge = 0, edgeOpaque = 0;
          for (var y = 0; y < h; y++) {
            for (var x = 0; x < w; x++) {
              var a = d[(y * w + x) * 4 + 3];
              if (x === 0 || y === 0 || x === w - 1 || y === h - 1) {
                edge++;
                if (a > 24) edgeOpaque++;
              }
              if (a > 24) {
                if (x < l) l = x;
                if (x > r) r = x;
                if (y < t) t = y;
                if (y > b) b = y;
              }
            }
          }
          var cut = r >= 0 && edge > 0 && edgeOpaque / edge < 0.03;
          resolve({ ar: w / h, cut: cut, l: l / w, t: t / h, r: (r + 1) / w, b: (b + 1) / h });
        } catch (e) {
          resolve(null);
        }
      };
      im.onerror = function () { resolve(null); };
      im.src = url;
    });
    return cache[url];
  }

  function fitValues(W, H, bb, opts) {
    opts = opts || {};
    var pw = opts.w || 0.9, ph = opts.h || 0.92, base = opts.base || 0.965, max = opts.max || 3;
    if (!bb || !bb.cut || !W || !H) return null;
    var rw, rh;
    if (W / H > bb.ar) { rh = H; rw = H * bb.ar; } else { rw = W; rh = W / bb.ar; }
    var ox = (W - rw) / 2, oy = (H - rh) / 2;
    var cw = rw * (bb.r - bb.l), ch = rh * (bb.b - bb.t);
    if (cw <= 0 || ch <= 0) return null;
    var s = Math.min((W * pw) / cw, (H * ph) / ch);
    s = Math.max(opts.min != null ? opts.min : 1, Math.min(s, max));
    var cx = ox + rw * (bb.l + bb.r) / 2;
    var cb = oy + rh * bb.b;
    var bottom = opts.floor ? H * base : Math.min(H * base, H / 2 + (s * ch) / 2 + H * 0.06);
    return {
      fx: -s * (cx - W / 2),
      fy: bottom - (H / 2 + s * (cb - H / 2)),
      fs: s,
      sw: Math.min(90, ((cw * s) / W) * 100 * 0.92)
    };
  }

  function apply(el, v, fallbackScale) {
    if (v) {
      el.style.setProperty('--fx', v.fx.toFixed(1) + 'px');
      el.style.setProperty('--fy', v.fy.toFixed(1) + 'px');
      el.style.setProperty('--fs', v.fs.toFixed(4));
      el.style.setProperty('--sw', v.sw.toFixed(1) + '%');
    } else {
      el.style.removeProperty('--fx');
      el.style.removeProperty('--fy');
      el.style.setProperty('--fs', String(fallbackScale || 1));
      el.style.removeProperty('--sw');
    }
  }

  function num(el, name, dflt) {
    var v = parseFloat(el.getAttribute(name));
    return isNaN(v) ? dflt : v;
  }

  function fitOne(el) {
    probe(el.getAttribute('data-photo-fit')).then(function (bb) {
      var v = fitValues(el.clientWidth, el.clientHeight, bb, {
        w: num(el, 'data-fit-w', 0.9), h: num(el, 'data-fit-h', 0.92),
        base: num(el, 'data-fit-base', 0.965), max: num(el, 'data-fit-max', 3),
        floor: el.getAttribute('data-fit-floor') === '1'
      });
      el.classList.toggle('is-cut', !!v);
      apply(el, v, 1);
    });
  }

  var ro = 'ResizeObserver' in window ? new ResizeObserver(function (entries) {
    entries.forEach(function (e) { fitOne(e.target); });
  }) : null;

  function auto(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll('[data-photo-fit]'), function (el) {
      if (el.__photoFit) return;
      el.__photoFit = true;
      fitOne(el);
      if (ro) ro.observe(el);
    });
  }

  window.CryonosPhotoFit = { probe: probe, fitValues: fitValues, apply: apply, auto: auto };
})();
